import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

const Slider = ({
  slides,
  hideNextPrevButton = false,
  whiteNextPrevButton = false,
  autoSlide = false,
  hideDots = false,
  onSlideChange,
  activeIndex,
}: {
  slides: ReactNode[];
  hideNextPrevButton?: boolean;
  whiteNextPrevButton?: boolean;
  autoSlide?: boolean;
  hideDots?: boolean;
  onSlideChange?: (index: number) => void;
  activeIndex?: number;
}) => {
  const isMobile = useIsMobile();
  const [current, setCurrent] = useState(1); // start at first real slide (because of clones)
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [startX, setStartX] = useState<number | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const total = slides.length;

  // internal index: 0 = cloneLast, 1..N = real slides, N+1 = cloneFirst
  const realIndex = (idx: number) => {
    if (idx === 0) return total - 1;
    if (idx === total + 1) return 0;
    return idx - 1;
  };

  const updateSlide = (newIndex: number) => {
    setCurrent(newIndex);
    onSlideChange?.(realIndex(newIndex));
  };

  const nextSlide = () => updateSlide(current + 1);
  const prevSlide = () => updateSlide(current - 1);

  // auto slide
  useEffect(() => {
    if (!autoSlide) return;
    const id = setInterval(() => nextSlide(), 3000);
    return () => clearInterval(id);
  }, [current, autoSlide]);

  // handle seamless jump after reaching clones
  const handleTransitionEnd = () => {
    if (current === 0) {
      setIsTransitioning(false);
      setCurrent(total);
      requestAnimationFrame(() => setTimeout(() => setIsTransitioning(true), 20));
    }
    if (current === total + 1) {
      setIsTransitioning(false);
      setCurrent(1);
      requestAnimationFrame(() => setTimeout(() => setIsTransitioning(true), 20));
    }
  };

  // swipe handling
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isMobile) setStartX(e.touches[0].clientX);
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isMobile && startX !== null) {
      const endX = e.changedTouches[0].clientX;
      if (startX - endX > 50) nextSlide();
      if (endX - startX > 50) prevSlide();
      setStartX(null);
    }
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isMobile) setStartX(e.clientX);
  };
  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isMobile && startX !== null) {
      const endX = e.clientX;
      if (startX - endX > 50) nextSlide();
      if (endX - startX > 50) prevSlide();
      setStartX(null);
    }
  };

  // build with clones
  const rendered = [
    slides[total - 1], // clone last
    ...slides,
    slides[0], // clone first
  ];

  return (
    <div
      className="relative w-full mx-auto overflow-hidden rounded-lg"
      ref={sliderRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div
        className={`flex ${isTransitioning ? "transition-transform duration-700 ease-in-out" : ""}`}
        style={{ transform: `translateX(-${current * 100}%)` }}
        onTransitionEnd={handleTransitionEnd}
      >
        {rendered.map((slide, idx) => (
          <div key={idx} className="flex-shrink-0 w-full">
            {slide}
          </div>
        ))}
      </div>

      {/* Controls */}
      {hideNextPrevButton || isMobile ? null : (
        <>
          <button
            onClick={prevSlide}
            className={cn(
              "absolute left-2 top-1/2 -translate-y-1/2 bg-transparent border !rounded-full hover:bg-black/20 cursor-pointer p-0 h-[42px] w-[42px] flex items-center justify-center",
              whiteNextPrevButton ? "border-white" : "border-[#2B63721A]"
            )}
          >
            <ChevronLeft className={cn(whiteNextPrevButton ? "stroke-white" : "stroke-[#2B6372]")} />
          </button>
          <button
            onClick={nextSlide}
            className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2 bg-transparent border !rounded-full hover:bg-black/20 cursor-pointer p-0 h-[42px] w-[42px] flex items-center justify-center",
              whiteNextPrevButton ? "border-white" : "border-[#2B63721A]"
            )}
          >
            <ChevronRight className={cn(whiteNextPrevButton ? "stroke-white" : "stroke-[#2B6372]")} />
          </button>
        </>
      )}

      {/* Dots */}
      {!hideDots || isMobile ? (
        <div className="absolute bottom-0 z-1 w-full flex justify-center gap-2">
          {slides.map((_, idx) => (
            <div
              key={idx}
              onClick={() => updateSlide(idx + 1)} // map real -> internal
              className={`h-2 w-2 rounded-full cursor-pointer ${
                realIndex(current) === idx ? "bg-primary" : "bg-gray-300"
              }`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default Slider;
